import { WAREHOUSE_COORDINATES } from '../config/warehouse'
import {
  OWN_DELIVERY_RECOMMENDED_RADIUS_KM,
  isWithinOwnDeliveryRecommendRadius,
} from '../constants/ownDelivery'
import {
  geocodeAddress,
  getRoadDistanceKm,
} from './distanceService'
import { getCountryCode } from '../utils/countryMapping'

export interface WarehouseDistanceAddress {
  street_name?: string
  house_number?: string
  postal_code?: string
  city?: string
  country?: string
}

export interface WarehouseRoadDistanceResult {
  distanceKm: number
  routeGeometry: number[][]
  destCoords: { lat: number; lon: number }
  destLabel: string
}

/**
 * Road distance + route from E-freight warehouse (Lindeboomseweg 57, Amersfoort)
 * to the delivery address — same Geoapify stack as the calculator.
 */
export async function getWarehouseRoadDistance(
  address: WarehouseDistanceAddress,
): Promise<WarehouseRoadDistanceResult | null> {
  const postal = address.postal_code?.trim()
  const city = address.city?.trim()
  const street = address.street_name?.trim()
  const house = address.house_number?.trim()
  if (!postal && !city) return null

  const countryRaw = address.country?.trim()
  const countryIso = countryRaw ? getCountryCode(countryRaw) : undefined

  const coords = await geocodeAddress({
    street: street || undefined,
    housenumber: house || undefined,
    postal_code: postal || undefined,
    city: city || undefined,
    country: countryIso || countryRaw || undefined,
  })
  if (!coords) return null

  const result = await getRoadDistanceKm(WAREHOUSE_COORDINATES, coords)
  if (result == null || typeof result.distanceKm !== 'number') return null

  const destLabel = [street, house, postal, city].filter(Boolean).join(', ') ||
    'Delivery address'

  return {
    distanceKm: result.distanceKm,
    routeGeometry: result.routeGeometry ?? [],
    destCoords: coords,
    destLabel,
  }
}

/** @deprecated Prefer getWarehouseRoadDistance for map + distance */
export async function getWarehouseRoadDistanceKm(
  address: WarehouseDistanceAddress,
): Promise<number | null> {
  const result = await getWarehouseRoadDistance(address)
  return result?.distanceKm ?? null
}

export {
  OWN_DELIVERY_RECOMMENDED_RADIUS_KM,
  isWithinOwnDeliveryRecommendRadius,
}
